import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CCollapse,
  CFormSelect,
  CLoadingButton,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheck,
  cilCheckAlt,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilChevronBottom,
  cilChevronTop,
  cilInfo,
  cilPencil,
  cilWarning,
  cilX,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import FixtureService from 'src/service/FixtureService'
import TeamService from 'src/service/TeamService'
import PlayerService from 'src/service/PlayerService'
import SquadImportFixture from './SquadImportFixture'

const WIZARD_STEPS = ['Upload File', 'Validate Squads', 'Review & Import']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// One page big enough to hold a whole game's reference data, so validation can run offline.
const REFERENCE_LIMIT = 2000

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  missing_fixture: {
    label: 'Missing Fixture ID',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_fixture: {
    label: 'Invalid Fixture ID',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  fixture_has_squad: {
    label: 'Squad Already Exists',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  missing_team_code: {
    label: 'Missing Team Code',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_team_code: {
    label: 'Invalid Team Code',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  team_not_in_fixture: {
    label: 'Team Not In Fixture',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  missing_player: {
    label: 'Missing Player ID',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_player: {
    label: 'Invalid Player ID',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  duplicate: { label: 'Duplicate Row', color: 'warning', icon: cilWarning, accent: '#b45309' },
}

const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

const guessColumn = (headers, patterns) =>
  headers.find((h) =>
    patterns.some((p) =>
      String(h)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .includes(p),
    ),
  ) || ''

const fixtureLabel = (fixture) => {
  if (!fixture) return ''
  const teams = [fixture.team_name, fixture.team2_name].filter(Boolean).join(' vs ')
  return [fixture.round ? `Round ${fixture.round}` : null, teams].filter(Boolean).join(' · ')
}

const upper = (value) => String(value ?? '').toUpperCase()

const buildRow = (base, ctx, seen) => {
  const { fixtureById, teamBySn, playerById } = ctx

  const fixtureId = String(base.fixture_id ?? '').trim()
  const teamCode = String(base.team_code ?? '').trim()
  const playerId = String(base.player_id ?? '').trim()

  const fixture = fixtureById.get(fixtureId)
  const team = teamBySn.get(upper(teamCode))
  const player = playerById.get(playerId)

  const row = {
    fixture_id: fixtureId,
    team_code: teamCode,
    player_id: playerId,
    fixture_label: fixtureLabel(fixture) || (fixtureId ? `Fixture #${fixtureId}` : ''),
    team_name: team?.name ?? '',
    player_name: player?.full_name ?? String(base.player_name ?? '').trim(),
    result: 'valid',
  }

  // Whether a fixture still has room for a squad is deliberately NOT checked here — that
  // answer goes stale the moment someone edits a squad in another tab. It is asked live in
  // runImport() instead, right before the file is submitted.
  if (!fixtureId) {
    row.result = 'missing_fixture'
  } else if (!fixture) {
    row.result = 'invalid_fixture'
  } else if (!teamCode) {
    row.result = 'missing_team_code'
  } else if (!team) {
    row.result = 'invalid_team_code'
  } else if (fixture && !fixtureHasTeam(fixture, teamCode)) {
    row.result = 'team_not_in_fixture'
  } else if (!playerId) {
    row.result = 'missing_player'
  } else if (!player) {
    row.result = 'invalid_player'
  } else if (seen.has(`${fixtureId}|${playerId}`)) {
    row.result = 'duplicate'
  } else {
    seen.add(`${fixtureId}|${playerId}`)
  }

  return row
}

// Skipped for a fixture whose listing row carried no team codes, so a thin record cannot
// wrongly reject an otherwise good row.
const fixtureHasTeam = (fixture, teamCode) => {
  const codes = [fixture.team_code, fixture.team2_code].filter(Boolean).map(upper)
  if (!codes.length) return true
  return codes.includes(upper(teamCode))
}

const buildValidatedRows = (baseRows, ctx) => {
  const seen = new Set()
  return baseRows.map((base, i) => {
    const r = buildRow(base, ctx, seen)
    return { ...r, row: i + 1, included: r.result === 'valid' }
  })
}

const MiniStepper = ({ current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {WIZARD_STEPS.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < WIZARD_STEPS.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

// The fixture ID / team code lookup. On the upload step it is what the admin copies from, so
// it starts open; on the validate step it is only a cross-check, so it starts closed and does
// not mount - or fetch - until someone opens it.
const FixtureLookupCard = ({ description, defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen)
  const [mounted, setMounted] = useState(defaultOpen)

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) setMounted(true)
  }

  return (
    <CCard className={`border-secondary-subtle rounded-4 ${className}`}>
      <CCardBody>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <div className="fw-semibold">Fixture ID &amp; Team Code</div>
            <div className="small text-medium-emphasis">{description}</div>
          </div>
          <CButton color="secondary" variant="outline" size="sm" onClick={toggle}>
            <CIcon icon={open ? cilChevronTop : cilChevronBottom} className="me-1" />
            {open ? 'Hide' : 'Show'}
          </CButton>
        </div>
        <CCollapse visible={open}>
          <div className="pt-3">{mounted && <SquadImportFixture />}</div>
        </CCollapse>
      </CCardBody>
    </CCard>
  )
}

// Squad Import: upload a spreadsheet of player-to-fixture rows, check each one against this
// game's real fixtures, teams and players, fix any of it inline, then commit only the rows
// left included. Mirrors the Fixture/Player/Round Import wizards' 3-step shape.
const SquadImport = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const [optionsLoading, setOptionsLoading] = useState(true)
  const [fixtures, setFixtures] = useState([])
  const [teams, setTeams] = useState([])
  const [players, setPlayers] = useState([])

  const [rows, setRows] = useState([])
  const [editingRow, setEditingRow] = useState(null)
  const [editDraft, setEditDraft] = useState({ fixture_id: '', team_code: '', player_id: '' })

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  useEffect(() => {
    setOptionsLoading(true)
    Promise.all([
      FixtureService.getFixture(0, REFERENCE_LIMIT, 1, new URLSearchParams()),
      TeamService.getTeamListByGrade(),
      PlayerService.getPlayers(0, REFERENCE_LIMIT, 1, new URLSearchParams()),
    ])
      .then(([fixtureRes, teamRes, playerRes]) => {
        setFixtures(fixtureRes?.data?.data || [])
        setTeams(teamRes?.status === 200 ? teamRes.data || [] : [])
        setPlayers(playerRes?.data?.data || [])
      })
      .catch(() => ToastComponent('Could not load fixture, team and player data.', 'error'))
      .finally(() => setOptionsLoading(false))
  }, [])

  const fixtureById = useMemo(() => {
    const map = new Map()
    fixtures.forEach((f) => map.set(String(f.id), f))
    return map
  }, [fixtures])

  const teamBySn = useMemo(() => {
    const map = new Map()
    teams.forEach((t) => t.sn && map.set(upper(t.sn), t))
    return map
  }, [teams])

  const playerById = useMemo(() => {
    const map = new Map()
    players.forEach((p) => map.set(String(p.id), p))
    return map
  }, [players])

  const validationCtx = useMemo(
    () => ({ fixtureById, teamBySn, playerById }),
    [fixtureById, teamBySn, playerById],
  )

  // Every fixture in the game, ordered by ID because that is what the spreadsheet quotes.
  const fixtureOptions = useMemo(
    () =>
      fixtures
        .map((f) => ({ value: String(f.id), label: `#${f.id} · ${fixtureLabel(f)}` }))
        .sort((a, b) => Number(a.value) - Number(b.value)),
    [fixtures],
  )

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setEditingRow(null)
    setImportErrors([])
  }

  const handleDownloadCsv = async () => {
    setDownloading(true)
    try {
      await FixtureService.squadPlayerExport()
    } finally {
      setDownloading(false)
    }
  }

  const handleFile = (file) => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      ToastComponent('File is too large. Maximum size is 10MB.', 'error')
      return
    }
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const detected = {
        fixture_id: guessColumn(rawHeaders, ['fixtureid', 'fixture', 'fid']),
        team_code: guessColumn(rawHeaders, ['teamcode', 'team']),
        player_id: guessColumn(rawHeaders, ['playerid', 'player']),
        player_name: guessColumn(rawHeaders, ['playername', 'fullname', 'name']),
      }

      // Rows with nothing in them are the blank lines spreadsheets leave behind, not data.
      const baseRows = json
        .map((r) => ({
          fixture_id: detected.fixture_id ? r[detected.fixture_id] : '',
          team_code: detected.team_code ? r[detected.team_code] : '',
          player_id: detected.player_id ? r[detected.player_id] : '',
          player_name: detected.player_name ? r[detected.player_name] : '',
        }))
        .filter((r) =>
          [r.fixture_id, r.team_code, r.player_id].some((v) => String(v).trim() !== ''),
        )

      setFileName(file.name)
      setFileRowCount(baseRows.length)
      setRows(buildValidatedRows(baseRows, validationCtx))
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (!optionsLoading) handleFile(e.dataTransfer.files?.[0])
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const startEdit = (r) => {
    setEditingRow(r.row)
    setEditDraft({ fixture_id: r.fixture_id, team_code: r.team_code, player_id: r.player_id })
  }

  const cancelEdit = () => {
    setEditingRow(null)
    setEditDraft({ fixture_id: '', team_code: '', player_id: '' })
  }

  // Re-validating the whole file keeps the duplicate check honest — an edit can create or
  // clear a clash with any other row, not just its own.
  const saveEdit = (row) => {
    setRows((prev) => {
      const baseRows = prev.map((r) =>
        r.row === row ? { ...r, ...editDraft, player_name: '' } : r,
      )
      const revalidated = buildValidatedRows(baseRows, validationCtx)
      return revalidated.map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
    cancelEdit()
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const affectedFixtures = new Set(readyRows.map((r) => r.fixture_id)).size

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])

    // The API refuses the whole file if any fixture in it already has players, and it says so
    // without naming one. Ask which fixtures are still empty at this exact moment - a snapshot
    // taken when the page loaded would be wrong the second anyone edits a squad elsewhere - so
    // a clash can be pinned to the rows that caused it.
    let emptyFixtureIds
    try {
      const res = await FixtureService.getSquadFixture()
      emptyFixtureIds = new Set((res?.data || []).map((f) => String(f.id)))
    } catch (e) {
      setImportErrors(['Could not check the current squads. Please try again.'])
      setImporting(false)
      return
    }

    const clashes = readyRows.filter((r) => !emptyFixtureIds.has(r.fixture_id))
    if (clashes.length) {
      const clashRows = new Set(clashes.map((r) => r.row))
      const clashFixtures = [...new Set(clashes.map((r) => `#${r.fixture_id}`))].join(', ')
      setRows((prev) =>
        prev.map((r) =>
          clashRows.has(r.row) ? { ...r, result: 'fixture_has_squad', included: false } : r,
        ),
      )
      setImportErrors([
        `${clashFixtures} already ${clashes.length === 1 ? 'has' : 'have'} a squad, so ${
          clashes.length
        } row${clashes.length === 1 ? '' : 's'} cannot be imported. Clear that squad from Manage ` +
          'Scores, or point those rows at another fixture, then try again.',
      ])
      setImporting(false)
      goToStep(2)
      return
    }

    const playerData = readyRows.map((r) => ({
      fixture_id: r.fixture_id,
      team_code: r.team_code,
      player_id: r.player_id,
    }))

    // The API discards the last row it is handed, a leftover from when the old CSV parser
    // passed it the blank line at the end of a file. Send a sentinel so a real player is
    // never the row that gets dropped.
    FixtureService.importSquadPlayer({ playerData: [...playerData, {}] })
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          navigate('/manage-scores')
        } else {
          setImportErrors(
            Array.isArray(res.errors) ? res.errors : [res.message || 'Something went wrong.'],
          )
        }
      })
      .catch(() => setImportErrors(['Something went wrong. Please try again.']))
      .finally(() => setImporting(false))
  }

  const renderUpload = () => (
    <>
      <CRow className="g-4">
        <CCol lg={5}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Download Template</div>
              <div className="small mb-3">
                The template lists every player in this game with their team code and player ID
                already filled in.
              </div>
              <CLoadingButton
                color="primary"
                variant="outline"
                className="mb-4"
                loading={downloading}
                onClick={handleDownloadCsv}
              >
                <CIcon icon={cilCloudDownload} className="me-1" />
                Download Squad Template
              </CLoadingButton>
              <div className="fw-semibold mb-2">Required columns</div>
              <ul className="ps-3 mb-3 small">
                <li>
                  Fixture ID <span className="text-medium-emphasis">(you fill this in)</span>
                </li>
                <li>Team Code</li>
                <li>Player ID</li>
              </ul>
              <InfoBox color="light">
                Team code and player ID come pre-filled, so the only column you fill in is the
                fixture ID of the match each player is playing in — look those up in the table
                below. Delete the rows for players you are not picking. A fixture that already has a
                squad cannot be imported into; clear its squad first.
              </InfoBox>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={7}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Upload Completed Spreadsheet</div>
              <div className="small mb-3">
                Drag and drop an XLSX or CSV file, or browse your computer.
              </div>

              {!fileName ? (
                <div
                  className="border rounded-4 d-flex flex-column align-items-center justify-content-center text-center p-4"
                  style={{
                    cursor: optionsLoading ? 'not-allowed' : 'pointer',
                    minHeight: 180,
                    backgroundColor: isDragging ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
                    borderStyle: 'dashed',
                    opacity: optionsLoading ? 0.6 : 1,
                  }}
                  onClick={() => !optionsLoading && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    if (!optionsLoading) setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
                  <div>
                    Drag and drop an XLSX or CSV file, or{' '}
                    <span className="text-primary">browse</span>
                  </div>
                  <div className="text-medium-emphasis small mt-1">Maximum file size 10MB</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    disabled={optionsLoading}
                    onChange={(e) => {
                      handleFile(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                </div>
              ) : (
                <CCard className="border-secondary-subtle rounded-3">
                  <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <CIcon icon={cilFile} className="text-success" size="xl" />
                      <div>
                        <div className="fw-semibold">{fileName}</div>
                        <div className="small text-success d-flex align-items-center gap-1">
                          <CIcon icon={cilCheckCircle} size="sm" />
                          {fileRowCount} row{fileRowCount === 1 ? '' : 's'} detected
                        </div>
                      </div>
                    </div>
                    <CButton color="link" className="text-danger p-0" onClick={resetAll}>
                      Remove
                    </CButton>
                  </CCardBody>
                </CCard>
              )}

              {(busy || optionsLoading) && (
                <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
                  <CSpinner size="sm" />
                  {optionsLoading
                    ? 'Loading fixtures, teams and players for this game…'
                    : 'Reading and validating your file…'}
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <FixtureLookupCard
        defaultOpen
        className="mt-4"
        description={
          <>
            Find the match each player is playing in, then copy its fixture ID into the
            <code className="mx-1">fixture_id</code>
            column of your spreadsheet.
          </>
        }
      />

      <div className="d-flex justify-content-between mt-4">
        <CButton color="secondary" variant="outline" onClick={() => navigate('/manage-scores')}>
          Cancel
        </CButton>
        <CButton color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-primary" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-medium-emphasis">{fileRowCount} rows</div>
            </div>
          </div>
          <CButton color="secondary" variant="outline" size="sm" onClick={resetAll}>
            Replace File
          </CButton>
        </CCardBody>
      </CCard>

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
      </div>

      <FixtureLookupCard
        className="mb-3"
        description="Check a fixture ID or team code without leaving this step."
      />

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 si-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Fixture</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Player</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const isEditing = editingRow === r.row
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.fixture_id}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, fixture_id: e.target.value }))
                          }
                        >
                          <option value="">Select fixture</option>
                          {editDraft.fixture_id &&
                            !fixtureOptions.some(
                              (o) => o.value === String(editDraft.fixture_id),
                            ) && (
                              <option value={editDraft.fixture_id}>
                                #{editDraft.fixture_id} · not found
                              </option>
                            )}
                          {fixtureOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        <>
                          <div className="fw-semibold">{r.fixture_id || '—'}</div>
                          {r.fixture_label && (
                            <div className="small text-medium-emphasis">{r.fixture_label}</div>
                          )}
                        </>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.team_code}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, team_code: e.target.value }))
                          }
                        >
                          <option value="">Select team</option>
                          {teams.map((t) => (
                            <option key={t.id} value={t.sn}>
                              {t.name} ({t.sn})
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        <>
                          <div>{r.team_name || '—'}</div>
                          {r.team_code && <code className="small">{r.team_code}</code>}
                        </>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.player_id}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, player_id: e.target.value }))
                          }
                        >
                          <option value="">Select player</option>
                          {players.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.full_name} (#{p.id})
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        <>
                          <div>{r.player_name || '—'}</div>
                          {r.player_id && <code className="small">{r.player_id}</code>}
                        </>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                    </CTableDataCell>

                    <CTableDataCell>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <CButton
                            size="sm"
                            color="success"
                            variant="outline"
                            onClick={() => saveEdit(r.row)}
                          >
                            <CIcon icon={cilCheck} />
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <CIcon icon={cilX} />
                          </CButton>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            title="Edit this row"
                            onClick={() => startEdit(r)}
                          >
                            <CIcon icon={cilPencil} />
                          </CButton>
                          {r.result === 'valid' && (
                            <CButton
                              size="sm"
                              color={r.included ? 'danger' : 'success'}
                              variant="outline"
                              title={r.included ? 'Exclude from import' : 'Include in import'}
                              onClick={() => toggleRowIncluded(r.row)}
                            >
                              {r.included ? 'Exclude' : 'Include'}
                            </CButton>
                          )}
                        </div>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light">
          Rows that need attention are left out of the import. Edit a row to fix it, or leave it —
          the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
        <StatTile
          label="Ready to Import"
          value={readyRows.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Fixtures" value={affectedFixtures} tone="info" icon={cilCheckAlt} />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 si-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Fixture</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Player</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyRows.map((r) => (
                <CTableRow key={r.row}>
                  <CTableDataCell className="ps-4">
                    <div className="fw-semibold">#{r.fixture_id}</div>
                    {r.fixture_label && (
                      <div className="small text-medium-emphasis">{r.fixture_label}</div>
                    )}
                  </CTableDataCell>
                  <CTableDataCell>{r.team_name || r.team_code}</CTableDataCell>
                  <CTableDataCell>{r.player_name || r.player_id}</CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          color="success"
          loading={importing}
          disabled={!readyRows.length}
          onClick={runImport}
        >
          <CIcon icon={cilCloudUpload} className="me-1" />
          Import {readyRows.length} Player{readyRows.length === 1 ? '' : 's'}
        </CLoadingButton>
      </div>
    </>
  )

  return (
    <>
      <style>{`
        .si-import-table > :not(caption) > * > * { padding-top: .85rem; padding-bottom: .85rem; }
      `}</style>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="fw-bold mb-0">Squad Import</h4>
          <div className="small text-medium-emphasis">
            Build squads for several fixtures at once from a spreadsheet.
          </div>
        </div>
        <CButton color="secondary" variant="outline" onClick={() => navigate('/manage-scores')}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Manage Scores
        </CButton>
      </div>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <MiniStepper current={wizardStep} maxReached={maxWizardStep} onStepClick={goToStep} />
          {importErrors.length > 0 && (
            <CAlert color="danger">
              <div className="fw-semibold mb-2">The import could not be run:</div>
              <ul className="mb-0 ps-3">
                {importErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </CAlert>
          )}
          {wizardStep === 1 && renderUpload()}
          {wizardStep === 2 && renderValidate()}
          {wizardStep === 3 && renderReview()}
        </CCardBody>
      </CCard>
    </>
  )
}

export default SquadImport
